import { MY_SERVICES_PATHS, MyServicesPicker } from '@/components/my-services-picker';
import { DASHBOARD_SECTIONS, scrollToDashboardSection } from '@/lib/dashboard-sections';
import { BriefcaseBusiness, FileText, House, ListChecks } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const navItemClassName = (active: boolean) =>
  cn(
    'flex min-h-12 w-full flex-col items-center justify-center gap-1 rounded-lg px-1 text-[11px] font-medium text-muted-foreground transition-colors',
    active && 'bg-states-base-bg-control text-brand-medium',
  );

const items = [
  {
    to: '/',
    labelKey: 'mobileNavigation.home',
    icon: House,
    isActive: (pathname: string) => pathname === '/',
  },
  {
    to: '/services/services-catalogue',
    labelKey: 'mobileNavigation.services',
    icon: BriefcaseBusiness,
    isActive: (pathname: string) => pathname.startsWith('/services'),
  },
  {
    to: '/dashboard/workflows',
    hash: DASHBOARD_SECTIONS.activeApplications,
    labelKey: 'mobileNavigation.trackRequests',
    icon: ListChecks,
    isActive: (pathname: string) =>
      pathname.startsWith('/dashboard') && !MY_SERVICES_PATHS.includes(pathname as (typeof MY_SERVICES_PATHS)[number]),
  },
] as const;

export function MobileBottomNavigation() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const myServicesActive = MY_SERVICES_PATHS.some((path) => pathname === path) || pathname.startsWith('/registry');

  return (
    <nav
      aria-label={t('mobileNavigation.label')}
      className="fixed inset-x-0 bottom-0 z-[1050] border-t border-border bg-white/95 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur md:hidden"
    >
      <ul className="mx-auto grid max-w-lg grid-cols-4">
        {items.map((item) => {
          const active = item.isActive(pathname);
          const Icon = item.icon;

          return (
            <li key={item.labelKey}>
              <Link
                to={item.to}
                hash={'hash' in item ? item.hash : undefined}
                aria-current={active ? 'page' : undefined}
                onClick={() => {
                  if (!('hash' in item) || !item.hash) return;
                  const sectionId = item.hash;
                  requestAnimationFrame(() => {
                    scrollToDashboardSection(sectionId);
                  });
                }}
                className={navItemClassName(active)}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span className="max-w-full truncate">{t(item.labelKey)}</span>
              </Link>
            </li>
          );
        })}
        <li>
          <MyServicesPicker
            trigger={
              <button
                type="button"
                aria-haspopup="dialog"
                aria-current={myServicesActive ? 'page' : undefined}
                className={navItemClassName(myServicesActive)}
              >
                <FileText className="h-5 w-5" aria-hidden="true" />
                <span className="max-w-full truncate">{t('mobileNavigation.myServices')}</span>
              </button>
            }
          />
        </li>
      </ul>
    </nav>
  );
}
